import React, {useEffect, useLayoutEffect, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useNavigate} from 'react-router-dom';
import {Row} from 'antd';
import {getAllApprovedAds, getAllApprovedJobs} from '../../../Store/Actions';
import {
  DEFAULT_CONTAINER_HEIGHT,
  addHttpsToUrl,
  cssClassText,
  getLocalStorageUserInfo,
  truncateTextToWords,
} from '../../../Util/commonUtility';
import view_more_svg from '../../../Assets/view-more-right-arrow.svg';
import './style.css';
import TabFilter from '../UserTabFilter';
import useLocalization from '../../../Localization';

/** Must match `.marquee--inner` in style.css */
const MARQUEE_DURATION_SEC = 20;

const CommonWantToSeeAdsListing = ({
  usedForChapPage,
  containerHeight = DEFAULT_CONTAINER_HEIGHT,
  usedForRegister,
  isVisible = true,
  tabKey = 'ads',
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {ApplicationString} = useLocalization();
  const [tabCommonKey, setTabCommonKey] = useState();

  const JobsAndAdsTabs = [
    {key: 'ads', label: ApplicationString['jobsAndAdsTabs.ads']},
    {
      key: 'jobs',
      label: ApplicationString['jobsAndAdsTabs.jobs'],
    },
  ];

  const adsData = useSelector(({ads}) => ads?.getApprovedAds?.data?.ads);
  const jobsData = useSelector(({jobs}) => jobs?.getApprovedJobs?.data?.jobs);

  const [shouldRender, setShouldRender] = useState(false);
  const [allAdsData, setAllAdsData] = useState([]);
  const [allJobsData, setAllJobsData] = useState([]);

  const isAdsVisible = (!shouldRender ? tabCommonKey : tabKey) === 'ads';

  const handleWindowResize = () => {
    setShouldRender(window.innerWidth > 1000);
  };

  const submitAdHandler = () => {
    const userInfo = getLocalStorageUserInfo();
    navigate(userInfo ? '/ads' : '/ads/approved');
  };

  const submitJobHandler = () => {
    const userInfo = getLocalStorageUserInfo();
    navigate(userInfo ? '/jobs' : '/login');
  };

  const viewApprovedJobsHandler = () => {
    navigate('/jobs/approved');
  };

  const renderCardList = (data, omitDomIds = false) =>
    data.map((item, i) => {
      const isLastItem =
        i === selectedData.length - 1 && !shouldRender && isAdsVisible;
      const attachmentUrl = isAdsVisible
        ? addHttpsToUrl(item?.attachment_url || '')
        : `/jobs/${item?.id}` || '';
      const title = isAdsVisible ? item?.vendor : item?.employer || '';
      return (
        <div
          key={i}
          className={`${
            isLastItem && 'margin20'
          } want-to-see-your-ads-listing-card`}>
          <span>
            <a
              id={omitDomIds ? undefined : `ads-link-${i}`}
              href={attachmentUrl}
              target='_blank'
              rel='noopener noreferrer'>
              {title}
            </a>{' '}
            {isAdsVisible ? (
              <>
                {' '}
                <span>{truncateTextToWords(item?.description || '', 20)}</span>
              </>
            ) : (
              <>
                <br />
                <span className='job-position-text'>{item?.position}</span>
                <br />
                <span
                  dangerouslySetInnerHTML={{
                    __html: truncateTextToWords(item?.description || '', 20),
                  }}
                />
              </>
            )}
          </span>
        </div>
      );
    });

  useEffect(() => {
    if (adsData) setAllAdsData(adsData);
    else dispatch(getAllApprovedAds());
  }, [adsData, dispatch]);

  useEffect(() => {
    if (jobsData) setAllJobsData(jobsData);
    else dispatch(getAllApprovedJobs());
  }, [jobsData, dispatch]);

  useEffect(() => {
    window.addEventListener('resize', handleWindowResize);
    handleWindowResize();
    return () => {
      window.removeEventListener('resize', handleWindowResize);
    };
  }, []);

  const selectedData = isAdsVisible ? allAdsData : allJobsData;

  const listViewportRef = useRef(null);
  const listPlainContentRef = useRef(null);
  const marqueeRef = useRef(null);
  const pendingMarqueeProgressRef = useRef(null);
  const [isMarqueePaused, setIsMarqueePaused] = useState(false);
  const [contentExceedsViewport, setContentExceedsViewport] = useState(false);

  const cappedListingHeight =
    typeof containerHeight === 'number' && Number.isFinite(containerHeight);

  // Only compare against a numeric cap (map/register). Login passes 'auto' —
  // must not measure against DEFAULT_CONTAINER_HEIGHT or layout breaks.
  const heightCap = cappedListingHeight
    ? containerHeight
    : usedForRegister
    ? DEFAULT_CONTAINER_HEIGHT
    : null;

  const useOffscreenMeasure = heightCap != null;

  useLayoutEffect(() => {
    const viewport = listViewportRef.current;
    const plain = listPlainContentRef.current;
    if (!viewport || !selectedData?.length) {
      setContentExceedsViewport(false);
      return undefined;
    }

    const measure = () => {
      if (useOffscreenMeasure && plain) {
        setContentExceedsViewport(plain.offsetHeight > heightCap + 1);
        return;
      }
      setContentExceedsViewport(
        viewport.scrollHeight > viewport.clientHeight + 1,
      );
    };

    measure();
    requestAnimationFrame(measure);
    const ro = new ResizeObserver(measure);
    ro.observe(viewport);
    if (plain) ro.observe(plain);
    return () => ro.disconnect();
  }, [selectedData, heightCap, isAdsVisible, useOffscreenMeasure]);

  useLayoutEffect(() => {
    const viewport = listViewportRef.current;
    const marquee = marqueeRef.current;
    if (viewport) viewport.scrollTop = 0;
    if (marquee) {
      marquee.style.animation = '';
      marquee.style.animationDelay = '';
      marquee.style.transform = '';
      delete marquee.dataset.paused;
    }
    pendingMarqueeProgressRef.current = null;
    setIsMarqueePaused(false);
  }, [isAdsVisible, selectedData]);

  /** Fixed viewport + marquee whenever content is taller than the slot (not only by row count). */
  const isOverflowingContent = contentExceedsViewport;

  const shouldShowScrollingContent =
    isOverflowingContent && shouldRender && isVisible;

  // Resume only after the duplicate span is back in the DOM; otherwise the
  // animation runs against half the loop height and looks progressively slower.
  useLayoutEffect(() => {
    if (!shouldShowScrollingContent || isMarqueePaused) return;
    const progress = pendingMarqueeProgressRef.current;
    if (progress == null) return;

    const marquee = marqueeRef.current;
    if (!marquee) return;

    marquee.style.animation = `marquee ${MARQUEE_DURATION_SEC}s linear infinite`;
    marquee.style.animationDelay = `-${progress * MARQUEE_DURATION_SEC}s`;
    pendingMarqueeProgressRef.current = null;
  }, [isMarqueePaused, shouldShowScrollingContent]);

  const pauseMarquee = () => {
    const viewport = listViewportRef.current;
    const marquee = marqueeRef.current;
    if (!viewport || !marquee || marquee.dataset.paused === '1') return;

    const {transform} = getComputedStyle(marquee);
    const offsetY =
      transform && transform !== 'none'
        ? new DOMMatrixReadOnly(transform).m42
        : 0;
    const loopHeight = listPlainContentRef.current?.offsetHeight;
    if (!loopHeight) return;

    marquee.dataset.paused = '1';
    marquee.style.animation = 'none';
    marquee.style.transform = 'none';
    viewport.scrollTop = Math.max(0, -offsetY) % loopHeight;
    setIsMarqueePaused(true);
  };

  const resumeMarquee = () => {
    const viewport = listViewportRef.current;
    const marquee = marqueeRef.current;
    const loopHeight = listPlainContentRef.current?.offsetHeight;
    if (!marquee || marquee.dataset.paused !== '1' || !loopHeight) return;

    const offsetInLoop = viewport ? viewport.scrollTop % loopHeight : 0;
    pendingMarqueeProgressRef.current = offsetInLoop / loopHeight;

    if (viewport) viewport.scrollTop = 0;
    marquee.style.animation = 'none';
    marquee.style.animationDelay = '';
    marquee.style.transform = '';
    delete marquee.dataset.paused;
    setIsMarqueePaused(false);
  };

  const containerClassName = [
    usedForRegister
      ? 'want-to-see-your-ads-container for-register'
      : 'want-to-see-your-ads-container',
    usedForChapPage && 'want-to-see-your-ads-container-margin-for-chapPage',
    !shouldRender && 'tab-view-mobile',
  ]
    .filter(Boolean)
    .join(' ');

  const listingContainerClassName = [
    usedForRegister
      ? 'want-to-see-your-ads-listing-container register'
      : 'want-to-see-your-ads-listing-container',
    // Login uses `isVisible={false}` and needs inner scrolling. Register already
    // has a fixed layout (`.register`) and we keep its hover behavior.
    ((!isVisible && !usedForRegister) || isMarqueePaused) &&
      'force-inner-scroll',
    // `.scrolling-none` uses `min-height: fit-content`, which defeats max-height and
    // stops overflow measurement — only use it when height is not capped numerically.
    !cappedListingHeight && !isOverflowingContent && 'scrolling-none',
    !shouldRender && 'tab-view-mobile',
  ]
    .filter(Boolean)
    .join(' ');

  const containerPosition =
    isOverflowingContent && isVisible ? 'absolute' : 'relative';

  let listingHeight = cssClassText.auto;
  if (!isVisible && cappedListingHeight) {
    listingHeight = containerHeight;
  } else if (isOverflowingContent) {
    listingHeight = containerHeight;
  }

  return (
    <>
      {!shouldRender && (
        <div className='align-right  padding-top20'>
          <TabFilter setTabKey={setTabCommonKey} tabs={JobsAndAdsTabs} />{' '}
        </div>
      )}
      <div className={containerClassName}>
        <div
          ref={listViewportRef}
          className={listingContainerClassName}
          onMouseEnter={shouldShowScrollingContent ? pauseMarquee : undefined}
          onMouseLeave={shouldShowScrollingContent ? resumeMarquee : undefined}
          style={{
            // Only lock height when content overflows (marquee needs a fixed
            // viewport). Otherwise shrink to content so short lists aren't
            // padded with blank space under maxHeight.
            height: listingHeight,
            maxHeight: containerHeight,
            marginBottom:
              isOverflowingContent && isVisible
                ? cssClassText.pixel32
                : cssClassText.pixel0,
          }}>
          {useOffscreenMeasure && selectedData?.length > 0 && (
            <div
              ref={listPlainContentRef}
              className='want-to-see-your-ads-offscreen-measure'
              aria-hidden>
              {renderCardList(selectedData, true)}
            </div>
          )}
          {shouldShowScrollingContent ? (
            <div
              ref={marqueeRef}
              className={`marquee--inner want-to-see-your-ads-marquee-layer ${
                usedForRegister && 'register-width'
              }`}>
              <span>{renderCardList(selectedData)}</span>
              {!isMarqueePaused && <span>{renderCardList(selectedData)}</span>}
            </div>
          ) : (
            <div ref={!useOffscreenMeasure ? listPlainContentRef : undefined}>
              {renderCardList(selectedData)}
            </div>
          )}
        </div>
        {isAdsVisible && (
          <Row
            className='want-to-see-your-ads-buttons-container'
            style={{
              position: containerPosition,
            }}>
            <button
              id='submit-ads-button'
              className='view-more-svg-button'
              onClick={submitAdHandler}>
              {ApplicationString['adsApprovedListComponent.button']}
              <img
                src={view_more_svg}
                alt='view_more_svg'
                className='view-more-svg'
              />
            </button>
          </Row>
        )}
        {!isAdsVisible && (
          <Row
            className='want-to-see-your-ads-buttons-container'
            style={{
              position: containerPosition,
            }}>
            <button
              id='submit-ads-button'
              className='view-more-svg-button'
              onClick={submitJobHandler}>
              {ApplicationString['jobsApprovedListComponent.button']}
            </button>
            <button
              id='view-approved-jobs-button'
              className='view-more-svg-button'
              onClick={viewApprovedJobsHandler}>
              {ApplicationString['userJobsComponent.button.viewMoreJobs']}
            </button>
          </Row>
        )}
      </div>
    </>
  );
};

export default CommonWantToSeeAdsListing;
